import { useEffect, useState } from "react";
import { Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ContentPasteSearchOutlinedIcon from "@mui/icons-material/ContentPasteSearchOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import RecentActorsOutlinedIcon from "@mui/icons-material/RecentActorsOutlined";
import HistoryIcon from "@mui/icons-material/History";
import InterpreterModeIcon from "@mui/icons-material/InterpreterMode";
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';

import {
  LabelItem,
  SideBarMenu,
  Overlay,
  ToggleButtonWrapper,
  FlexContainer
} from "./styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../contexts";
import { Permissions } from "../../modules/users/models";
import Tooltip from "../tooltip";
import permissionsStore from "../../store/permissions";

const AppSidebar = () => {
  const { permissions: userPermissions } = permissionsStore();
  const { logout } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  const pathname = window.location.pathname;
  const firstSegment = "/" + pathname.split("/")[1];
  const [activeItem, setActiveItem] = useState(firstSegment);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const updatedPathname = window.location.pathname;
    const firstSegmentPath = "/" + updatedPathname.split("/")[1];
    setActiveItem(firstSegmentPath);
  }, [location.pathname]);

  const handleMenuClick = (item: string) => {
    setActiveItem(item);
    navigate(item);
  };

  const hasPermission = (permission: Permissions) => {
    return userPermissions.includes(permission);
  };

  return (
    <>
      {/* Botão de toggle sempre visível no mobile */}
      <ToggleButtonWrapper>
        <IconButton onClick={() => setCollapsed(!collapsed)}>
          <MenuIcon className="!w-10 !h-10 !text-borderCreditsDiv" />
        </IconButton>
      </ToggleButtonWrapper>

      <SideBarMenu collapsed={collapsed}>
        {/* Para desktop, o Box com o botão pode ficar dentro do menu */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            justifyContent: collapsed ? "center" : "flex-end",
            padding: "10px"
          }}
        >
          <IconButton onClick={() => setCollapsed(!collapsed)}>
            <MenuIcon
              className="!w-10 !h-10 !text-borderCreditsDiv"
              sx={{ color: "white" }}
            />
          </IconButton>
        </Box>

        <FlexContainer>
          <Menu
            rootStyles={{
              display: "flex",
              flexDirection: "column"
            }}
            menuItemStyles={{
              button: ({ active }) => ({
                backgroundColor: active ? "#231F20" : "transparent",
                "&:hover": {
                  backgroundColor: "#231F20"
                },
                color: "#fff"
              }),
              icon: { color: "#fff" },
              label: { marginTop: "2px" }
            }}
          >
            <Tooltip
              title={collapsed ? "Consultar" : ""}
              arrow
              placement="right-end"
            >
              <MenuItem
                icon={<HomeOutlinedIcon />}
                active={activeItem === "/home"}
                onClick={() => {
                  setCollapsed(true);
                  handleMenuClick("/home");
                }}
              >
                <LabelItem>Home</LabelItem>
              </MenuItem>
            </Tooltip>

            <Tooltip
              title={collapsed ? "Consultar" : ""}
              arrow
              placement="right-end"
            >
              <MenuItem
                icon={<ContentPasteSearchOutlinedIcon />}
                active={activeItem === "/consult"}
                onClick={() => {
                  setCollapsed(true);
                  handleMenuClick("/consult");
                }}
              >
                <LabelItem>Consultar</LabelItem>
              </MenuItem>
            </Tooltip>

            {hasPermission(Permissions.ACCOUNTS) && (
              <Tooltip
                title={collapsed ? "Contas" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<PeopleAltOutlinedIcon />}
                  active={activeItem === "/accounts"}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/accounts");
                  }}
                >
                  <LabelItem>Contas</LabelItem>
                </MenuItem>
              </Tooltip>
            )}

            {hasPermission(Permissions.SCENARIOS) && (
              <Tooltip
                title={collapsed ? "Critérios" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<GavelOutlinedIcon />}
                  active={activeItem === "/scenarios"}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/scenarios");
                  }}
                >
                  <LabelItem>Critérios</LabelItem>
                </MenuItem>
              </Tooltip>
            )}

            {hasPermission(Permissions.USERS) && (
              <Tooltip
                title={collapsed ? "Usuários" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<RecentActorsOutlinedIcon />}
                  active={activeItem === "/users"}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/users");
                  }}
                >
                  <LabelItem>Usuários</LabelItem>
                </MenuItem>
              </Tooltip>
            )}

            {hasPermission(Permissions.CREDITS) && (
              <Tooltip
                title={collapsed ? "Créditos" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<CreditCardOutlinedIcon />}
                  active={activeItem === "/credits"}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/credits");
                  }}
                >
                  <LabelItem>Créditos</LabelItem>
                </MenuItem>
              </Tooltip>
            )}
            {hasPermission(Permissions.AMBASSADOR) && (
              <Tooltip
                title={collapsed ? "Embaixador" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<GroupsIcon />}
                  active={activeItem === "/ambassador"}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/ambassador");
                  }}
                >
                  <LabelItem>Embaixador</LabelItem>
                </MenuItem>
              </Tooltip>
            )}
            {hasPermission(Permissions.HISTORY) && (
              <Tooltip
                title={collapsed ? "Histórico" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<HistoryIcon />}
                  active={activeItem === "/history"}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/history");
                  }}
                >
                  <LabelItem>Histórico</LabelItem>
                </MenuItem>
              </Tooltip>
            )}
          </Menu>

          <Menu
            menuItemStyles={{
              button: ({ active }) => ({
                backgroundColor: active ? "#231F20" : "transparent",
                "&:hover": { backgroundColor: "#231F20" },
                color: "#fff"
              }),
              icon: { color: "#fff" },
              label: { marginTop: "2px" }
            }}
          >
            {hasPermission(Permissions.SUPPORT) && (
              <Tooltip
                title={collapsed ? "Suporte" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<InterpreterModeIcon />}
                  active={activeItem === "/suporte"}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/suporte");
                  }}
                >
                  <LabelItem>Suporte</LabelItem>
                </MenuItem>
              </Tooltip>
            )}
            {hasPermission(Permissions.BI) && (
              <Tooltip
                title={collapsed ? "Relatórios" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<TrendingUpOutlinedIcon />}
                  active={activeItem.startsWith("/reports")}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/reports/dashboard");
                  }}
                >
                  <LabelItem>Relatórios</LabelItem>
                </MenuItem>
              </Tooltip>
            )}
            {hasPermission(Permissions.CONFIGURATIONS) && (
              <Tooltip
                title={collapsed ? "Configurações" : ""}
                arrow
                placement="right-end"
              >
                <MenuItem
                  icon={<SettingsOutlinedIcon />}
                  active={activeItem === "/configurations"}
                  onClick={() => {
                    setCollapsed(true);
                    handleMenuClick("/configurations");
                  }}
                >
                  <LabelItem>Configurações</LabelItem>
                </MenuItem>
              </Tooltip>
            )}

            <Tooltip
              title={collapsed ? "Sair" : ""}
              arrow
              placement="right-end"
            >
              <MenuItem
                icon={<LogoutOutlinedIcon />}
                active={false}
                onClick={logout}
              >
                <LabelItem>Sair</LabelItem>
              </MenuItem>
            </Tooltip>
          </Menu>
        </FlexContainer>
      </SideBarMenu>

      {/* Overlay para fechar o menu ao clicar fora (somente mobile) */}
      {!collapsed && <Overlay onClick={() => setCollapsed(true)} />}
    </>
  );
};

export default AppSidebar;
